import React, { useState, useEffect, useCallback } from 'react';
import { User, Users, CreditCard, Shield, Settings, MapPin, Plus, Home, Trash2, Beer, Edit, LogOut, Mail, Loader, ChevronDown, ChevronRight, Droplets } from 'lucide-react';
import { MAINTENANCE_ITEMS } from './kegops-utils';
import * as BreweryService from './services/breweryService';
import * as SupabaseService from './supabaseService';
import { supabase } from './supabaseClient';

// ===== COLLAPSIBLE SECTION COMPONENT =====
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false, iconColor = 'text-amber-600' }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors rounded-lg"
      >
        <h2 className="text-xl font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
          {Icon && <Icon className={`w-6 h-6 ${iconColor}`} />}
          {title}
        </h2>
        {isOpen ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
      </button>
      {isOpen && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

// ===== ROLE DEFINITIONS =====
const BREWERY_ROLES = [
  { value: 'bar_back', label: 'Bar Back', emoji: '📦', description: 'Limited access - basic tasks' },
  { value: 'bartender', label: 'Bartender', emoji: '🍺', description: 'Can edit tap status and fill kegs' },
  { value: 'bar_manager', label: 'Manager', emoji: '🔧', description: 'Full access except billing' },
  { value: 'bar_owner', label: 'Owner', emoji: '👑', description: 'Full administrative access' }
];

// ===== INVITE BREWERY MEMBER FORM COMPONENT =====
const InviteBreweryMemberForm = ({ breweryId, onInviteSent, onCancel, hasOwner }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('bartender');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasOwner && role === 'bar_owner') {
      setRole('bartender');
    }
  }, [hasOwner, role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const result = await BreweryService.inviteBarMember(breweryId, email, role, name);
    
    if (result.success) {
      alert(`Invitation sent to ${email}!`);
      setEmail('');
      setName('');
      setRole('bartender');
      onInviteSent();
    } else {
      setError(result.error || 'Failed to send invitation');
    }
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-amber-50 dark:bg-gray-700 rounded-lg border border-amber-200 dark:border-gray-600">
      <div>
        <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100 text-sm">Email Address*</label>
        <input 
          type="email" 
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
          placeholder="brewmember@example.com"
        />
      </div>
      
      <div>
        <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100 text-sm">Name (Optional)</label>
        <input 
          type="text" 
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
          placeholder="John Doe"
        />
      </div>

      <div>
        <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100 text-sm">Permission Level*</label>
        <select 
          value={role} 
          onChange={(e) => setRole(e.target.value)}
          className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
        >
          {BREWERY_ROLES.map(r => (
            <option 
              key={r.value} 
              value={r.value}
              disabled={r.value === 'bar_owner' && hasOwner}
            >
              {r.emoji} {r.label} - {r.value === 'bar_owner' && hasOwner ? 'Only one owner per brewery' : r.description}
            </option>
          ))}
        </select>
        {hasOwner && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Only one owner per brewery. Transfer ownership by changing the existing owner.</p>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button 
          type="submit"
          disabled={isLoading || !email}
          className="flex-1 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 disabled:bg-gray-400 transition font-medium text-sm"
        >
          {isLoading ? 'Sending...' : 'Send Invitation'}
        </button>
        <button 
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-900 dark:text-gray-100 py-2 rounded hover:bg-gray-400 dark:hover:bg-gray-500 transition text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// ===== BREWERY MEMBERS DISPLAY COMPONENT =====
const BreweryMembersDisplay = ({ breweryId, breweryName, selectedBreweryId, onMembersLoaded, currentUserRole }) => {
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  const loadMembers = useCallback(async () => {
    setIsLoading(true);
    const { data: userData } = await supabase.auth.getUser();
    const authUser = userData?.user;
    if (authUser) setCurrentUser(authUser);

    const [membersResult, invitesResult] = await Promise.all([
      BreweryService.getBarMembers(breweryId, breweryName),
      BreweryService.getPendingInvites(breweryId)
    ]);

    let membersData = membersResult.success ? membersResult.data : [];

    if (authUser) {
      const alreadyIncluded = membersData.some(m => m.userId === authUser.id);
      if (!alreadyIncluded) {
        membersData = [
          ...membersData,
          {
            membershipId: `self-${authUser.id}`,
            userId: authUser.id,
            role: currentUserRole || 'bar_owner',
            email: authUser.email,
            name: authUser.user_metadata?.name || authUser.email,
            createdAt: authUser.created_at
          }
        ];
      }
    }

    setMembers(membersData);
    if (invitesResult.success) setPendingInvites(invitesResult.data);
    setIsLoading(false);
    onMembersLoaded?.(membersData);
  }, [breweryId, breweryName, onMembersLoaded, currentUserRole]);

  useEffect(() => {
    loadMembers();
  }, [breweryId, selectedBreweryId, loadMembers]);

  const handleRemoveMember = async (membershipId) => {
    if (!window.confirm('Remove this member from the brewery?')) return;
    
    const result = await BreweryService.removeBarMember(membershipId);
    if (result.success) {
      setMembers(members.filter(m => m.membershipId !== membershipId));
    } else {
      alert('Failed to remove member: ' + result.error);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Cancel this invitation?')) return;
    
    const result = await BreweryService.deletePendingInvite(inviteId);
    if (result.success) {
      setPendingInvites(pendingInvites.filter(i => i.id !== inviteId));
      console.log('✅ Invitation canceled and deleted');
    } else {
      alert('Failed to cancel invitation: ' + result.error);
      console.error('❌ Failed to delete invite:', result.error);
    }
  };

  const getRoleDisplay = (role) => {
    const roleObj = BREWERY_ROLES.find(r => r.value === role);
    return roleObj ? `${roleObj.emoji} ${roleObj.label}` : role;
  };

  if (isLoading) {
    return <div className="text-gray-500 dark:text-gray-400 text-sm">Loading members...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Active Members */}
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-sm">Active Members</h3>
        {members.length > 0 ? (
          <div className="space-y-2">
            {members.map(member => (
              <div key={member.membershipId} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition">
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {member.name || member.email}
                    {member.userId === currentUser?.id && (
                      <span className="ml-2 px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">You</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{member.email}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-green-100 dark:bg-green-900 dark:bg-opacity-30 text-green-800 dark:text-green-300 rounded text-xs font-medium">
                    {getRoleDisplay(member.role)}
                  </span>
                  <button 
                    onClick={() => member.userId === currentUser?.id ? null : handleRemoveMember(member.membershipId)} 
                    className={`text-red-600 hover:text-red-700 transition ${member.userId === currentUser?.id ? 'opacity-30 cursor-not-allowed' : ''}`}
                    title={member.userId === currentUser?.id ? 'You cannot remove yourself' : 'Remove member'}
                    disabled={member.userId === currentUser?.id}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">No members yet</div>
        )}
      </div>

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div className="border-t pt-4 dark:border-gray-600">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-sm">Pending Invitations</h3>
          <div className="space-y-2">
            {pendingInvites.map(invite => (
              <div key={invite.id} className="flex items-center justify-between p-3 border border-amber-200 dark:border-amber-800 rounded bg-amber-50 dark:bg-amber-900 dark:bg-opacity-20 hover:bg-amber-100 dark:hover:bg-amber-900 dark:hover:bg-opacity-30 transition">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-600" />
                    <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">{invite.invited_name || invite.invite_name || invite.invite_email}</div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 ml-6">{invite.invited_name || invite.invite_name ? invite.invite_email + ' • ' : ''}Invited as {getRoleDisplay(invite.role)}</div>
                </div>
                <button 
                  onClick={() => handleCancelInvite(invite.id)} 
                  className="text-amber-600 hover:text-amber-700 transition"
                  title="Cancel invitation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== PENDING INVITES FOR USER COMPONENT =====
const UserPendingInvitesSection = ({ onInviteAccepted }) => {
  const [invites, setInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    loadPendingInvites();
  }, []);

  const loadPendingInvites = async () => {
    setIsLoading(true);
    const result = await BreweryService.getUserPendingInvites();
    if (result.success) {
      setInvites(result.data);
    }
    setIsLoading(false);
  };

  const handleAcceptInvite = async (token, inviteId) => {
    setProcessingId(inviteId);
    const result = await BreweryService.acceptInvite(token);
    
    if (result.success) {
      // Remove from local list
      setInvites(prev => prev.filter(i => i.id !== inviteId));
      // Notify parent to reload breweries
      if (onInviteAccepted) {
        onInviteAccepted();
      }
      alert('Invite accepted! You now have access to the brewery.');
    } else {
      alert('Failed to accept invite: ' + (result.error || 'Unknown error'));
    }
    setProcessingId(null);
  };

  const handleDeclineInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to decline this invitation?')) {
      return;
    }
    
    setProcessingId(inviteId);
    const result = await BreweryService.declineInvite(inviteId);
    
    if (result.success) {
      setInvites(prev => prev.filter(i => i.id !== inviteId));
    } else {
      alert('Failed to decline invite: ' + (result.error || 'Unknown error'));
    }
    setProcessingId(null);
  };

  const getRoleDisplay = (role) => {
    const roleInfo = BREWERY_ROLES.find(r => r.value === role);
    return roleInfo ? `${roleInfo.emoji} ${roleInfo.label}` : role;
  };

  if (isLoading) {
    return null; // Don't show loading state, just hide if no invites
  }

  if (invites.length === 0) {
    return null; // Don't show section if no pending invites
  }

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg shadow p-6 mb-6">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-900 dark:text-gray-100">
        <Mail className="w-6 h-6 text-amber-600" />
        Pending Brewery Invitations
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        You have been invited to join the following breweries:
      </p>
      
      <div className="space-y-3">
        {invites.map(invite => (
          <div 
            key={invite.id} 
            className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex items-center justify-between"
          >
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {invite.breweryName}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Role: {getRoleDisplay(invite.role)}
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500">
                Expires: {new Date(invite.expires_at).toLocaleDateString()}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleAcceptInvite(invite.token, invite.id)}
                disabled={processingId === invite.id}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition"
              >
                {processingId === invite.id ? 'Processing...' : 'Accept'}
              </button>
              <button
                onClick={() => handleDeclineInvite(invite.id)}
                disabled={processingId === invite.id}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition"
              >
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ===== USER PROFILE SECTION COMPONENT =====
const UserProfileSection = () => {
  const [profile, setProfile] = useState({
    name: '',
    email: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      
      setProfile({
        name: data.name || '',
        email: user.email || ''
      });
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setError(null);
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('profiles')
        .update({
          name: profile.name,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;
      setSuccessMessage('Profile saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to save profile: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <p className="text-gray-600 dark:text-gray-400">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-6 flex items-center gap-2 text-gray-900 dark:text-gray-100">
        <User className="w-6 h-6 text-amber-600" />
        User Profile
      </h2>
      
      <div className="space-y-4">
        <div>
          <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Your Name*</label>
          <input 
            type="text" 
            value={profile.name}
            onChange={(e) => setProfile({...profile, name: e.target.value})}
            className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Your full name"
          />
        </div>

        <div>
          <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Email</label>
          <input 
            type="email" 
            value={profile.email}
            disabled
            className="w-full border rounded px-3 py-2 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-600 dark:text-gray-400 cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Email cannot be changed here. Update in your auth settings.</p>
        </div>

        {error && (
          <div className="p-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded text-sm">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="p-3 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded text-sm">
            {successMessage}
          </div>
        )}

        <button 
          onClick={handleSaveProfile}
          disabled={isSaving}
          className="w-full bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 font-medium transition disabled:bg-gray-400"
        >
          {isSaving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
};

export const AccountView = ({
  account,
  setAccount,
  breweries,
  setBreweries,
  selectedBreweryId,
  setSelectedBreweryId,
  onLogoUpload,
  setCurrentView
}) => {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [membersRefreshKey, setMembersRefreshKey] = useState(0);
  const [hasOwner, setHasOwner] = useState(false);
  const [currentUserName, setCurrentUserName] = useState('');
  
  // Create brewery form state
  const [showCreateBrewery, setShowCreateBrewery] = useState(false);
  const [newBreweryName, setNewBreweryName] = useState('');
  const [isCreatingBrewery, setIsCreatingBrewery] = useState(false);
  const [createBreweryError, setCreateBreweryError] = useState('');
  
  const selectedBrewery = breweries?.find(b => b.id === selectedBreweryId);
  const canManageMembers = selectedBrewery?.userRole === 'bar_manager' || selectedBrewery?.userRole === 'bar_owner' || selectedBrewery?.userRole === 'super_admin';
  // For super admin, find Brewer Born; for regular users, find all breweries where they have bar_owner role
  const ownedBreweries = breweries?.filter(b => {
    // Super admin can manage Brewer Born
    if (b.userRole === 'super_admin') return b.name === 'Brewer Born';
    // Regular users: find all breweries where they have bar_owner role
    return b.userRole === 'bar_owner';
  }) || [];
  const canEditSelectedBrewery = selectedBrewery?.userRole === 'bar_owner' || (selectedBrewery?.userRole === 'super_admin' && selectedBrewery?.name === 'Brewer Born');
  
  // Local form state for brewery profile editing - initialize from selected brewery
  const [profileForm, setProfileForm] = useState({
    name: selectedBrewery?.name || '',
    // Prefer the brewery's own default_owner, then fall back to the brewery's name, then account default
    defaultOwner: selectedBrewery?.default_owner || selectedBrewery?.name || account.profile?.defaultOwner || '',
    logoUrl: selectedBrewery?.logo_url || ''
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Sync local form when selected brewery changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedBrewery) {
      setProfileForm({
        name: selectedBrewery.name || '',
        // If brewery has no explicit default_owner, use the brewery's name so new kegs default to this brewery
        defaultOwner: selectedBrewery.default_owner || selectedBrewery.name || account.profile?.defaultOwner || '',
        logoUrl: selectedBrewery.logo_url || ''
      });
      setHasUnsavedChanges(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBreweryId, selectedBrewery?.name, selectedBrewery?.default_owner, selectedBrewery?.logo_url, account.profile?.defaultOwner]);

  useEffect(() => {
    const loadUserName = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const authUser = userData?.user;
      if (authUser) {
        setCurrentUserName(authUser.user_metadata?.name || authUser.email || 'Owner');
      }
    };
    loadUserName();
  }, []);

  // Handle profile form field changes
  const handleProfileChange = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  // Handle logo upload to local form state
  const handleLocalLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileForm(prev => ({ ...prev, logoUrl: reader.result }));
        setHasUnsavedChanges(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setProfileForm(prev => ({ ...prev, logoUrl: '' }));
    setHasUnsavedChanges(true);
  };

  // Save brewery profile - saves to the selected brewery, not global account
  const handleSaveProfile = async () => {
    if (!selectedBreweryId) {
      alert('No brewery selected');
      return;
    }
    setIsSavingProfile(true);
    try {
      // Update the brewery record in the database
      const result = await BreweryService.updateBrewerySettings(selectedBreweryId, {
        name: profileForm.name,
        default_owner: profileForm.defaultOwner,
        logo_url: profileForm.logoUrl
      });
      
      if (result.success) {
        // Update the local breweries state to reflect the change
        setBreweries(prev => prev.map(b => 
          b.id === selectedBreweryId 
            ? { ...b, name: profileForm.name, default_owner: profileForm.defaultOwner, logo_url: profileForm.logoUrl }
            : b
        ));
        setHasUnsavedChanges(false);
        alert('Brewery profile saved successfully!');
      } else {
        alert('Error saving profile: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error saving profile: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Handle creating a new brewery
  const handleCreateBrewery = async () => {
    if (!newBreweryName.trim()) {
      setCreateBreweryError('Please enter a brewery name');
      return;
    }

    if (newBreweryName.trim().length < 2) {
      setCreateBreweryError('Brewery name must be at least 2 characters');
      return;
    }

    setIsCreatingBrewery(true);
    setCreateBreweryError('');

    try {
      const result = await BreweryService.createFirstFreeBrewery(account.user.id, newBreweryName.trim());
      
      if (result.success) {
        // Add new brewery to breweries list
        const newBrewery = {
          ...result.data,
          userRole: 'bar_owner',
          membershipId: undefined
        };
        setBreweries(prev => [...prev, newBrewery]);
        setSelectedBreweryId(result.data.id);
        setCurrentView('dashboard');
        setShowCreateBrewery(false);
        setNewBreweryName('');
      } else {
        setCreateBreweryError(result.error || 'Failed to create brewery');
      }
    } catch (err) {
      setCreateBreweryError('Error creating brewery: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsCreatingBrewery(false);
    }
  };

  // Warn before navigating away with unsaved changes
  const handleNavigateAway = (destination) => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return;
      }
    }
    setCurrentView(destination);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white h-24">
        <div className="max-w-6xl mx-auto flex items-center justify-between h-full px-6">
          <div className="flex items-center gap-3 h-full">
            <img src="/kegops-logo.png" alt="KegOps" className="h-full object-contain" />
            <div className="flex flex-col justify-center">
              <h1 className="text-3xl font-bold leading-tight">Account Management</h1>
              <p className="text-sm text-amber-100">Manage your brewery profile and members</p>
            </div>
          </div>
          {account?.profile?.logoUrl && (
            <div className="h-20 w-20 rounded-full border border-amber-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
              <img src={account.profile.logoUrl} alt="Brewery" className="h-full w-full object-contain" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <button 
              onClick={() => handleNavigateAway('dashboard')} 
              className="flex items-center gap-2 bg-amber-800 hover:bg-amber-900 px-3 py-2 rounded transition"
            >
              <Home className="w-4 h-4" />Dashboard
            </button>
            <button 
              onClick={async () => {
                if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Are you sure you want to logout?')) {
                  return;
                }
                await supabase.auth.signOut();
              }}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 px-3 py-2 rounded transition"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Pending Invites for User (shows above everything if any) */}
        <UserPendingInvitesSection onInviteAccepted={() => {
          // Reload the page to refresh brewery list
          window.location.reload();
        }} />

        {/* User Profile Section */}
        <UserProfileSection />

        {/* Brewery Profile Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Beer className="w-6 h-6 text-amber-600" />
            Brewery Profile
          </h2>

          {/* Owner view: full edit controls */}
          {canEditSelectedBrewery ? (
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Brewery Logo</label>
                <div className="flex items-center gap-4">
                  {profileForm.logoUrl ? (
                    <img src={profileForm.logoUrl} alt="Brewery Logo" className="h-16 w-16 object-contain border rounded" />
                  ) : (
                    <div className="h-16 w-16 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center">
                      <Beer className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={handleLocalLogoUpload}
                      className="w-full border rounded px-3 py-2"
                    />
                    <p className="text-xs text-gray-500">Upload your brewery logo (JPG, PNG, etc.)</p>
                    {profileForm.logoUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition"
                      >
                        Remove Logo
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Brewery/Business Name</label>
                <input 
                  type="text" 
                  value={profileForm.name}
                  onChange={(e) => handleProfileChange('name', e.target.value)}
                  className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Owner Name</label>
                <input 
                  type="text" 
                  value={currentUserName}
                  disabled
                  className="w-full border rounded px-3 py-2 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">This is tied to your user profile name. Update it in your profile to change.</p>
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Default Brewery for New Kegs</label>
                <input 
                  type="text" 
                  value={profileForm.defaultOwner}
                  onChange={(e) => handleProfileChange('defaultOwner', e.target.value)}
                  className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>

              {hasUnsavedChanges && (
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded text-sm text-amber-800 dark:text-amber-200">
                  You have unsaved changes.
                </div>
              )}

              <button 
                onClick={handleSaveProfile}
                disabled={isSavingProfile}
                className="w-full bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 font-medium transition disabled:bg-gray-400"
              >
                {isSavingProfile ? 'Saving...' : 'Save Brewery Profile'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-gray-700 rounded border border-amber-200 dark:border-gray-600">
                <p className="text-gray-900 dark:text-gray-100 font-medium">You're viewing a brewery you don't own.</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">Switch back to your brewery to manage its profile and members.</p>
              </div>

              {ownedBreweries.length > 0 ? (
                <div className="space-y-2">
                  {ownedBreweries.length === 1 ? (
                    <>
                      <label className="block font-medium text-gray-900 dark:text-gray-100">Your Brewery Name</label>
                      <div className="w-full border rounded px-3 py-2 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                        {ownedBreweries[0].name}
                      </div>
                      <button
                        onClick={() => {
                          setSelectedBreweryId(ownedBreweries[0].id);
                          setCurrentView('dashboard');
                        }}
                        className="w-full bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 font-medium transition"
                      >
                        Change to Your Brewery
                      </button>
                    </>
                  ) : (
                    <>
                      <label className="block font-medium text-gray-900 dark:text-gray-100">Switch to Your Brewery</label>
                      <select
                        value=""
                        onChange={(e) => {
                          if (e.target.value) {
                            setSelectedBreweryId(e.target.value);
                            setCurrentView('dashboard');
                          }
                        }}
                        className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      >
                        <option value="">Select a brewery to switch to...</option>
                        {ownedBreweries.map(brewery => (
                          <option key={brewery.id} value={brewery.id}>
                            {brewery.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        You own {ownedBreweries.length} breweries. Select one to switch to.
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
                    <p className="text-green-900 dark:text-green-100 font-medium">Create Your Brewery</p>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-2">
                      Start with our free plan and upgrade anytime. Includes up to 2 kegs, 1 location, and 2 team members.
                    </p>
                  </div>

                  {!showCreateBrewery ? (
                    <button
                      onClick={() => setShowCreateBrewery(true)}
                      className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-medium transition"
                    >
                      Create Your Brewery
                    </button>
                  ) : (
                    <div className="space-y-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded">
                      <div>
                        <label className="block font-medium text-green-900 dark:text-green-100 mb-2">Brewery Name</label>
                        <input
                          type="text"
                          value={newBreweryName}
                          onChange={(e) => setNewBreweryName(e.target.value)}
                          placeholder="e.g., Local Craft Brewery"
                          maxLength={50}
                          disabled={isCreatingBrewery}
                          className="w-full border border-green-300 dark:border-green-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>

                      {createBreweryError && (
                        <div className="p-3 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded text-sm text-red-800 dark:text-red-200">
                          {createBreweryError}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <button
                          onClick={handleCreateBrewery}
                          disabled={isCreatingBrewery || !newBreweryName.trim()}
                          className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700 font-medium transition disabled:bg-gray-400 flex items-center justify-center gap-2"
                        >
                          {isCreatingBrewery && <Loader className="w-4 h-4 animate-spin" />}
                          {isCreatingBrewery ? 'Creating...' : 'Create Brewery'}
                        </button>
                        <button
                          onClick={() => {
                            setShowCreateBrewery(false);
                            setNewBreweryName('');
                            setCreateBreweryError('');
                          }}
                          disabled={isCreatingBrewery}
                          className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400 dark:hover:bg-gray-500 font-medium transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Brewery Members Section */}
        {selectedBreweryId && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-amber-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Brewery Members</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{selectedBrewery?.name}</p>
                </div>
              </div>
              {canManageMembers && (
                <button 
                  onClick={() => setShowInviteForm(!showInviteForm)}
                  className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 transition text-sm"
                >
                  <Plus className="w-4 h-4" />Invite Brewery Member
                </button>
              )}
            </div>

            {showInviteForm && canManageMembers && (
              <InviteBreweryMemberForm 
                breweryId={selectedBreweryId}
                hasOwner={hasOwner}
                onInviteSent={() => {
                  setShowInviteForm(false);
                  setMembersRefreshKey(prev => prev + 1);
                }}
                onCancel={() => setShowInviteForm(false)}
              />
            )}

            <div className={showInviteForm ? 'mt-6 pt-6 border-t dark:border-gray-700' : ''}>
              <BreweryMembersDisplay 
                key={membersRefreshKey}
                breweryId={selectedBreweryId} 
                breweryName={selectedBrewery?.name}
                selectedBreweryId={selectedBreweryId}
                onMembersLoaded={(membersData) => setHasOwner(membersData.some(m => m.role === 'bar_owner'))}
                currentUserRole={selectedBrewery?.userRole}
              />
            </div>
          </div>
        )}

        {/* QR Permissions Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Shield className="w-6 h-6 text-amber-600" />
            QR Code Permissions
          </h2>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3">
              <input 
                type="checkbox" 
                checked={account.qrPermissions.publicAccess}
                onChange={(e) => setAccount({
                  ...account, 
                  qrPermissions: {...account.qrPermissions, publicAccess: e.target.checked}
                })}
                className="w-5 h-5"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">Public Access</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Anyone can scan QR codes to view kegs</div>
              </div>
            </label>

            <label className="flex items-center gap-3">
              <input 
                type="checkbox" 
                checked={account.qrPermissions.viewableWithoutAuth}
                onChange={(e) => setAccount({
                  ...account, 
                  qrPermissions: {...account.qrPermissions, viewableWithoutAuth: e.target.checked}
                })}
                className="w-5 h-5"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">Viewable Without Login</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Users can view keg details without authentication</div>
              </div>
            </label>

            <label className="flex items-center gap-3">
              <input 
                type="checkbox" 
                checked={account.qrPermissions.editableWithoutAuth}
                onChange={(e) => setAccount({
                  ...account, 
                  qrPermissions: {...account.qrPermissions, editableWithoutAuth: e.target.checked}
                })}
                className="w-5 h-5"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">Editable Without Login</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Users can edit keg information without authentication</div>
              </div>
            </label>
          </div>
        </div>

        {/* Billing Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <CreditCard className="w-6 h-6 text-amber-600" />
            Subscription & Billing
          </h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Current Plan:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{account.subscription.plan}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Status:</span>
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900 dark:bg-opacity-30 text-green-800 dark:text-green-300 rounded text-sm">{account.subscription.status}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Next Billing Date:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{account.subscription.nextBilling}</span>
            </div>
            
            <button className="w-full mt-4 bg-amber-600 text-white py-2 rounded hover:bg-amber-700 transition">
              Manage Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export const SettingsView = ({
  settings,
  setSettings,
  account,
  theme,
  setTheme,
  locations,
  onAddLocation,
  onEditLocation,
  onAddSublocation,
  onEditSublocation,
  onDeleteSublocation,
  onDeleteLocation,
  setCurrentView,
  selectedBreweryId,
  breweries,
  setBreweries
}) => {
  const breweryLogo = account?.profile?.logoUrl || settings?.profile?.logoUrl;
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white h-24">
        <div className="max-w-6xl mx-auto flex items-center justify-between h-full px-6">
          <div className="flex items-center gap-3 h-full">
            <img src="/kegops-logo.png" alt="KegOps" className="h-full object-contain" />
            <div className="flex flex-col justify-center">
              <h1 className="text-3xl font-bold leading-tight">Settings</h1>
              <p className="text-sm text-amber-100">Configure locations, themes, and preferences</p>
            </div>
          </div>
          {breweryLogo && (
            <div className="h-20 w-20 rounded-full border border-amber-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
              <img src={breweryLogo} alt="Brewery" className="h-full w-full object-contain" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setCurrentView('dashboard')} 
              className="flex items-center gap-2 bg-amber-800 hover:bg-amber-900 px-3 py-2 rounded transition"
            >
              <Home className="w-4 h-4" />Dashboard
            </button>
            <button 
              onClick={async () => await supabase.auth.signOut()}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 px-3 py-2 rounded transition"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Manage Locations Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <MapPin className="w-6 h-6 text-amber-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Manage Locations</h2>
            </div>
            <button 
              onClick={onAddLocation} 
              className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-700 transition"
            >
              <Plus className="w-4 h-4" />Add Location
            </button>
          </div>

          <div className="space-y-4">
            {locations.map(loc => (
              <div key={loc.id} className="border border-gray-200 dark:border-gray-600 rounded p-4 dark:bg-gray-700">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="font-medium text-lg text-gray-900 dark:text-gray-100">{loc.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{loc.description}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => onEditLocation && onEditLocation(loc)} 
                      className="text-blue-600 hover:text-blue-700 transition"
                      title="Edit Location"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => onAddSublocation(loc.id)} 
                      className="text-green-600 hover:text-green-700 transition"
                      title="Add Sublocation"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={() => onDeleteLocation && onDeleteLocation(loc.id)} 
                      className="text-red-600 hover:text-red-700 transition"
                      title="Delete Location"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {loc.sublocations.length > 0 && (
                  <div className="ml-4 space-y-2">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400">Sublocations:</div>
                    {loc.sublocations.map(sub => (
                      <div key={sub.id} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-600 rounded">
                        <div>
                          <div className="font-medium text-sm text-gray-900 dark:text-gray-100">→ {sub.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{sub.description}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => onEditSublocation && onEditSublocation(loc.id, sub)} 
                            className="text-blue-600 hover:text-blue-700 transition"
                            title="Edit Sublocation"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => onDeleteSublocation(loc.id, sub.id)} 
                            className="text-red-600 hover:text-red-700 transition"
                            title="Delete Sublocation"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* System Settings Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Settings className="w-6 h-6 text-amber-600" />
            Display & Theme
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block mb-3 font-medium text-gray-900 dark:text-gray-100">Appearance</label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setTheme('light')}
                  className={`p-3 rounded-lg border-2 transition ${
                    theme === 'light'
                      ? 'border-amber-600 bg-amber-50 dark:bg-amber-900'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-amber-400'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">☀️</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Light</div>
                  </div>
                </button>
                
                <button
                  onClick={() => setTheme('dark')}
                  className={`p-3 rounded-lg border-2 transition ${
                    theme === 'dark'
                      ? 'border-amber-600 bg-amber-50 dark:bg-amber-900'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-amber-400'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">🌙</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Dark</div>
                  </div>
                </button>
                
                <button
                  onClick={() => setTheme('system')}
                  className={`p-3 rounded-lg border-2 transition ${
                    theme === 'system'
                      ? 'border-amber-600 bg-amber-50 dark:bg-amber-900'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-amber-400'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-1">⚙️</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">System</div>
                  </div>
                </button>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                System will use your device's light/dark mode setting
              </p>
            </div>
          </div>
        </div>

        {/* System Settings Section */}
        <CollapsibleSection title="System Settings" icon={Settings}>
          <div className="space-y-4">
            <div>
              <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Volume Units</label>
              <select 
                value={settings.volumeUnit} 
                onChange={(e) => setSettings({...settings, volumeUnit: e.target.value})} 
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="gal">Gallons (gal)</option>
                <option value="L">Liters (L)</option>
              </select>
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Weight Units</label>
              <select 
                value={settings.weightUnit} 
                onChange={(e) => setSettings({...settings, weightUnit: e.target.value})} 
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="lb">Pounds (lb)</option>
                <option value="kg">Kilograms (kg)</option>
              </select>
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-900 dark:text-gray-100">Beer Density (lb/gal)</label>
              <input 
                type="number" 
                step="0.01" 
                value={settings.beerDensity} 
                onChange={(e) => setSettings({...settings, beerDensity: parseFloat(e.target.value)})} 
                className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent" 
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Default: 8.43 lb/gal (used to calculate full keg weight)</p>
            </div>
          </div>
        </CollapsibleSection>

        {/* Maintenance Intervals Section */}
        <CollapsibleSection title="Maintenance Intervals" icon={Settings}>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Set intervals for maintenance reminders. Leave both fields blank for "Not Applicable".
            The lowest interval (time or batches) will trigger notifications.
            Line Cleaning applies to locations and sublocations that have taps enabled and will count a "batch" each time a beer is put on tap at that location.
          </p>
          
          <div className="space-y-6">
            {MAINTENANCE_ITEMS.map(item => {
              const interval = settings.maintenanceIntervals[item];
              return (
                <div key={item} className="border-b dark:border-gray-700 pb-4 last:border-b-0">
                  <h3 className="font-medium mb-3 text-gray-900 dark:text-gray-100">{item}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Time Interval</label>
                      <select 
                        value={interval?.months || ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? null : parseInt(e.target.value);
                          setSettings({
                            ...settings,
                            maintenanceIntervals: {
                              ...settings.maintenanceIntervals,
                              [item]: { ...interval, months: val }
                            }
                          });
                        }}
                        className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      >
                        <option value="">Not Applicable</option>
                        {[3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36, 39, 42, 45, 48, 51, 54, 57, 60].map(months => (
                          <option key={months} value={months}>
                            {months} months ({Math.floor(months / 12)} yr{months >= 24 ? 's' : ''} {months % 12 > 0 ? `${months % 12} mo` : ''})
                          </option>
                        ))}
                        <option value="999">Never</option>
                      </select>
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Number of Batches</label>
                      <input 
                        type="number" 
                        min="0"
                        value={interval?.batches || ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? null : parseInt(e.target.value);
                          setSettings({
                            ...settings,
                            maintenanceIntervals: {
                              ...settings.maintenanceIntervals,
                              [item]: { ...interval, batches: val }
                            }
                          });
                        }}
                        className="w-full border rounded px-3 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="Leave blank for N/A"
                      />
                    </div>
                  </div>
                  {!interval?.months && !interval?.batches && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">Not applicable - no interval set</p>
                  )}
                </div>
              );
            })}
          </div>
        </CollapsibleSection>

        {/* Water Filtration Settings */}
        <CollapsibleSection title="Water Filtration" icon={Droplets}>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Configure the water filtration setup for this brewery and suggested inspection intervals/methods.</p>

          <WaterFiltrationPanel selectedBreweryId={selectedBreweryId} breweries={breweries} onBreweriesUpdate={setBreweries} />
        </CollapsibleSection>

        {/* Maintenance History */}
        <CollapsibleSection title="Maintenance History" icon={Settings}>
          <MaintenanceHistory selectedBreweryId={selectedBreweryId} />
        </CollapsibleSection>

      </div>
    </div>
  );
};

// WaterFiltrationPanel - per-brewery configuration for filter types and inspection settings
const FILTER_TYPES = [
  { key: 'sediment', label: 'Sediment Filter', desc: 'Captures particulates. Check for pressure drop and replace or backwash when flow reduces or differential pressure increases. Typical interval: ~3 months.' },
  { key: 'carbon', label: 'Carbon Block', desc: 'Removes chlorine and organics that affect taste/odor. Replace on schedule or when taste/odor returns. Typical interval: ~6 months.' },
  { key: 'ro', label: 'RO Membrane', desc: 'Reverse Osmosis membrane. Monitor post-RO PPM and replace per manufacturer recommendations (often 12–36 months) or if PPM rises above threshold.' },
  { key: 'di', label: 'DI', desc: 'Deionization resin targeting near-0 PPM. Monitor PPM and replace/regen when PPM rises above target or resin shows contamination.' }
];

const WATER_MAINTENANCE_TYPES = [
  { key: 'inspection', label: 'Inspection', desc: 'Visual check or PPM measurement' },
  { key: 'cleaning', label: 'Cleaning', desc: 'Backwash, rinse, or sanitize' },
  { key: 'replacing', label: 'Replacing', desc: 'Replace filter cartridge/media' }
];
// WATER_MAINTENANCE_TYPES is exported for use in MassMaintenanceModal
export { WATER_MAINTENANCE_TYPES };

const CHECK_METHODS = [
  { key: 'inspection', label: 'Visual Inspection (color/condition)', desc: 'Look for discoloration, sediment, or visible damage. Good for filters that show color change.' },
  { key: 'ppm', label: 'PPM measurement (RO/DI)', desc: 'Measure conductivity/PPM downstream; target depends on system (e.g., RO < 50 PPM, DI ≈ 0 PPM).' },
  { key: 'pressure', label: 'Pressure check (sediment/carbon)', desc: 'Check differential pressure across the cartridge to detect clogging or reduced flow.' }
];

const WaterFiltrationPanel = ({ selectedBreweryId, breweries, onBreweriesUpdate }) => {
  const [form, setForm] = React.useState({ enabled: false, types: [], intervalMonths: 6, method: 'inspection', ppmThreshold: 50, notes: '' });
  const [isSaving, setIsSaving] = React.useState(false);
  const [showGuidance, setShowGuidance] = React.useState(false);
  const [validationError, setValidationError] = React.useState('');

  React.useEffect(() => {
    const b = breweries?.find(b => b.id === selectedBreweryId);
    if (!b) return;
    const wf = b.water_filtration || {};
    setForm({
      enabled: wf.enabled || false,
      types: wf.types || [],
      intervalMonths: wf.intervalMonths || 6,
      method: wf.method || 'inspection',
      ppmThreshold: wf.ppmThreshold || 50,
      notes: wf.notes || ''
    });
  }, [selectedBreweryId, breweries]);

  const handleToggleType = (t) => {
    setForm(prev => ({ ...prev, types: prev.types.includes(t) ? prev.types.filter(x => x !== t) : [...prev.types, t] }));
    setValidationError('');
  };

  const handleToggleEnabled = (enabled) => {
    setForm(prev => ({ ...prev, enabled }));
    setValidationError('');
  };

  const handleSave = async () => {
    if (!selectedBreweryId) return alert('Select a brewery');
    
    // Validation: if enabled, require at least one filter type
    if (form.enabled && form.types.length === 0) {
      setValidationError('Please select at least one filter type when water filtration is enabled.');
      return;
    }
    
    setIsSaving(true);
    setValidationError('');
    const updates = { water_filtration: { enabled: form.enabled, types: form.types, intervalMonths: form.intervalMonths, method: form.method, ppmThreshold: form.ppmThreshold, notes: form.notes } };
    const result = await BreweryService.updateBrewerySettings(selectedBreweryId, updates);
    setIsSaving(false);
    if (result.success) {
      // Update local breweries state if callback provided
      if (onBreweriesUpdate) {
        onBreweriesUpdate(prev => prev.map(b => b.id === selectedBreweryId ? { ...b, water_filtration: updates.water_filtration } : b));
      }
      alert('Water filtration settings saved');
    } else {
      alert('Failed to save: ' + result.error);
    }
  };

  const selectedDescriptions = FILTER_TYPES.filter(f => form.types.includes(f.key)).map(f => f.desc);

  return (
    <div className="space-y-4">
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50 dark:bg-gray-700">
        <div>
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Water Filtration System</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">Enable this if your brewery has a water filtration system (RO/DI, carbon, sediment, etc.)</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => handleToggleEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-500 peer-checked:bg-amber-600"></div>
          <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">{form.enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </div>

      {!form.enabled ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Droplets className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Enable water filtration above to configure your filter stages and maintenance intervals.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Filter Types Present</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Select the filter cartridges used at this brewery. <span className="text-red-500">*At least one required</span></p>
            </div>
            <button type="button" onClick={() => setShowGuidance(s => !s)} className="text-sm text-amber-600 hover:underline">{showGuidance ? 'Hide Guidance' : 'Show Guidance'}</button>
          </div>

          {validationError && (
            <div className="p-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded text-sm">
              {validationError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex gap-2 flex-wrap">
                {FILTER_TYPES.map(f => (
                  <label key={f.key} className={`flex items-start gap-2 px-2 py-1 rounded border cursor-pointer ${form.types.includes(f.key) ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700' : 'bg-gray-50 dark:bg-gray-700'}`} title={f.desc}>
                    <input aria-label={`Enable ${f.label}`} type="checkbox" checked={form.types.includes(f.key)} onChange={() => handleToggleType(f.key)} />
                    <div>
                  <div className="text-sm font-medium">{f.label}</div>
                  <div className="text-xs text-gray-500">{f.desc}</div>
                </div>
              </label>
            ))}
          </div>

          {form.types.length > 0 && (
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              <div className="font-medium">Selected filters:</div>
              <div className="text-xs mt-1">{selectedDescriptions.join(' • ')}</div>
            </div>
          )}

        </div>

        <div>
          <label className="block mb-2 font-medium text-sm">Suggested Inspection Interval (months)</label>
          <input type="number" min={0} className="w-32 border rounded px-2 py-1" value={form.intervalMonths} onChange={(e) => setForm({...form, intervalMonths: parseInt(e.target.value||0)})} />
          <p className="text-xs text-gray-500 mt-1">Suggested defaults: Sediment 3 mo, Carbon 6 mo, RO Membrane 24 mo, DI replace as needed</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block mb-2 font-medium text-sm">Check Method</label>
          <select className="border rounded px-3 py-2 w-full" value={form.method} onChange={(e) => setForm({...form, method: e.target.value})}>
            {CHECK_METHODS.map(m => (<option key={m.key} value={m.key}>{m.label}</option>))}
          </select>
          <p className="text-xs text-gray-500 mt-2">{(CHECK_METHODS.find(m => m.key === form.method) || {}).desc}</p>
        </div>
        <div>
          <label className="block mb-2 font-medium text-sm">PPM Threshold (RO/DI)</label>
          <input type="number" min={0} className="w-32 border rounded px-2 py-1" value={form.ppmThreshold} onChange={(e) => setForm({...form, ppmThreshold: parseInt(e.target.value||0)})} />
          <p className="text-xs text-gray-500 mt-1">Use when method is PPM; typical targets: RO &lt; 50 PPM, DI &approx; 0 PPM. Current selection: {form.method}</p>
        </div>
      </div>

      <div>
        <label className="block mb-2 font-medium text-sm">Notes / Methodology</label>
        <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full border rounded px-3 py-2" rows={3} placeholder="Describe how to check, e.g. PPM meter location, where to check pressure, visual color guide"></textarea>
      </div>

        </>
      )}

      <div className="flex gap-2 mt-4">
        <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700">{isSaving ? 'Saving...' : 'Save Water Filtration'}</button>
        <button onClick={() => setForm({ enabled: false, types: [], intervalMonths: 6, method: 'inspection', ppmThreshold: 50, notes: '' })} className="px-4 py-2 bg-gray-200 rounded">Reset</button>
      </div>

    </div>
  );
};

// MaintenanceHistory - lists recent location maintenance records
const MaintenanceHistory = ({ selectedBreweryId }) => {
  const [history, setHistory] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      if (!selectedBreweryId) return setIsLoading(false);
      setIsLoading(true);
      const res = await SupabaseService.fetchLocationMaintenanceRecords(selectedBreweryId, 100);
      if (res.success) setHistory(res.data || []);
      setIsLoading(false);
    };
    load();
  }, [selectedBreweryId]);

  if (!selectedBreweryId) return <div className="text-sm text-gray-500">Select a brewery to view maintenance history.</div>;
  if (isLoading) return <div className="text-sm text-gray-500">Loading maintenance history...</div>;
  if (!history || history.length === 0) return <div className="text-sm text-gray-500">No maintenance records yet.</div>;

  return (
    <div className="space-y-2">
      {history.map(h => (
        <div key={h.id} className="p-3 border rounded bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-sm">{h.location}{h.sublocation ? ` → ${h.sublocation}` : ''}</div>
              <div className="text-xs text-gray-500">{new Date(h.maintenance_date).toLocaleString()}</div>
            </div>
            <div className="text-xs text-gray-600">Batch snapshot: <span className="font-semibold">{h.batch_count_snapshot ?? 0}</span></div>
          </div>
          <div className="text-sm mt-2">Items: {(h.items || []).join(', ')}</div>
          {h.notes && <div className="text-sm text-gray-500 mt-1">Notes: {h.notes}</div>}
        </div>
      ))}
    </div>
  );
};